// src/modules/app-views/invoices/pages/Document/modals/ItemModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Row, Col, Descriptions, Typography, Divider, Alert, Form, theme } from 'antd';

const { useToken } = theme;
import SimpleSelectAsyncProducts from 'components/SimpleComponents/SimpleSelectAsyncProducts';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleCurrencyPercentage from 'components/SimpleComponents/SimpleCurrencyPercentage';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';
import fiscalService from 'services/fiscalService';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const ProductStepContent = ({
  selectedProduct,
  onProductSelect,
  onValueChange,
  zeroValueValidation,
  documentType,
  subtotal,
  form,
}) => (
  <Row gutter={[16, 16]}>
    <Col xs={24}>
      <SimpleSelectAsyncProducts
        name="productId"
        label="product"
        placeholder="search_product"
        onProductSelect={onProductSelect}
        selectedProduct={selectedProduct}
        required
      />
    </Col>

    {selectedProduct && (
      <>
        <Col xs={24}>
          <ProductDetails product={selectedProduct} />
        </Col>

        <Col xs={24}>
          <Divider style={{ margin: '8px 0 16px' }} />
        </Col>

        {/* Quantity, Unit Value, Discount and Total fields */}
        <Col xs={12} sm={6}>
          <SimpleNumber
            name="quantity"
            label="quantity"
            min={0.001}
            decimalPlaces={3}
            onChange={onValueChange}
          />
        </Col>

        <Col xs={12} sm={6}>
          <SimpleCurrency
            name="unitValue"
            label="unit_value"
            min={0}
            currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
            onChange={onValueChange}
          />
        </Col>

        <Col xs={12} sm={6}>
          <SimpleCurrencyPercentage
            name="discount"
            label="discount"
            base={subtotal || 0}
            init="percentage"
            currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
            onChange={onValueChange}
          />
        </Col>

        <Col xs={12} sm={6}>
          <SimpleCurrency
            name="totalValue"
            label="total"
            currencies={[{ value: 'BRL', label: 'R$', text: 'Real Brasileiro' }]}
            disabled
          />
        </Col>

        {/* Zero-value validation alert */}
        {zeroValueValidation?.type && (
          <Col xs={24}>
            <Alert
              type={zeroValueValidation.type}
              showIcon
              title={translateX(zeroValueValidation.error || zeroValueValidation.warning)}
              description={
                zeroValueValidation.error === 'nfe_zero_value_requires_cfop'
                  ? translateX('nfe_zero_value_requires_cfop_description')
                  : zeroValueValidation.error === 'nfce_zero_value_not_allowed'
                    ? translateX('nfce_zero_value_not_allowed_description')
                    : null
              }
              style={{ marginTop: 8 }}
            />
          </Col>
        )}
      </>
    )}
  </Row>
);

// *******************************************************************************
// PRODUCT DETAILS COMPONENT
// *******************************************************************************
const ProductDetails = ({ product }) => {
  if (!product) return null;

  const { token } = useToken();
  const { sku, ean, unit, description, brand } = product;

  // Handle unit object format:
  // - Hydrated from lookup: { uid, name, description }
  // - Legacy Brazilian fiscal: { comercial, tributavel }
  const unitValue = typeof unit === 'object' && unit !== null
    ? (unit.name ?? unit.comercial ?? unit.tributavel ?? '')
    : (unit ?? '');

  const descriptionValue = typeof description === 'object' && description !== null
    ? (description.comercial ?? description.tributavel ?? '')
    : (description ?? '');

  // Price can be in product.price, product.pricing.salePrice, or product.pricing.costPrice
  // Also handle object format for price
  const rawPrice = product.price || product.pricing?.salePrice || product.pricing?.costPrice || 0;
  const price = typeof rawPrice === 'object' && rawPrice !== null
    ? (rawPrice.comercial ?? rawPrice.tributavel ?? 0)
    : (rawPrice ?? 0);

  // NCM can be at root level or in fiscalData
  const ncm = product.ncm || product.fiscalData?.ncm;
  const hasMissingNcm = !ncm;

  return (
    <>
      {hasMissingNcm && (
        <Alert
          type="warning"
          title={translateX('ncm_missing_warning')}
          description={translateX('ncm_missing_warning_description')}
          showIcon
          style={{ marginTop: 8, marginBottom: 8 }}
        />
      )}

      <Descriptions
        column={{ xs: 1, sm: 2, md: 4 }}
        size="small"
        bordered
        style={{ marginTop: 8 }}
        styles={{ label: { width: 80, fontWeight: 500 } }}
      >
        {/* First row: SKU, EAN, unit, price */}
        <Descriptions.Item label={translateX('sku')}>{sku || '-'}</Descriptions.Item>
        <Descriptions.Item label="EAN">{ean || '-'}</Descriptions.Item>
        <Descriptions.Item label={translateX('unit')}>{unitValue || '-'}</Descriptions.Item>
        <Descriptions.Item label={translateX('price')}>
          <Text strong style={{ color: token.colorSuccess }}>
            {price ? financialService.formatCurrency(price) : '-'}
          </Text>
        </Descriptions.Item>

        {/* Second row: Brand, NCM, Description */}
        <Descriptions.Item label={translateX('brand')}>{brand || '-'}</Descriptions.Item>
        <Descriptions.Item label="NCM">
          <Text type={hasMissingNcm ? 'danger' : undefined} strong={hasMissingNcm}>
            {ncm || translateX('not_registered')}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label={translateX('description')} span={2}>
          <Text ellipsis style={{ maxWidth: 300 }}>
            {descriptionValue || '-'}
          </Text>
        </Descriptions.Item>
      </Descriptions>
    </>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const productStep = ({
  selectedProduct,
  setSelectedProduct,
  storedCompany,
  issuer,
  documentType,
  isNfce,
  fiscalConfig,
  setCalculatedTaxes,
  zeroValueValidation,
  setZeroValueValidation,
}) => ({
  stepTitle: 'product',
  stepTooltip: 'step1_tooltip',
  validationFields: ['productId'],
  content: ({ form }) => {
    // Watch values for subtotal calculation
    const quantity = Form.useWatch('quantity', form) || 1;
    const unitValue = Form.useWatch('unitValue', form) || 0;
    const subtotal = financialService.roundCurrency(quantity * unitValue);

    // Helper to recalculate total and taxes
    const recalculateTotalAndTaxes = () => {
      if (!form) return;

      const qty = form.getFieldValue('quantity') || 1;
      const price = form.getFieldValue('unitValue') || 0;
      const discount = form.getFieldValue('discount') || {
        init: 'percentage',
        percentage: 0,
        value: 0,
      };

      // Calculate subtotal and apply discount value
      const itemSubtotal = financialService.roundCurrency(qty * price);
      const discountValue = discount.value || 0;
      const totalValue = financialService.roundCurrency(Math.max(0, itemSubtotal - discountValue));

      // Update total in form
      form.setFieldsValue({ totalValue });

      // Validate zero-value based on document type and CFOP
      const cfop = form.getFieldValue('cfop');
      const validation = fiscalService.validateItemZeroValue({
        documentType: documentType || 'nfe',
        totalValue,
        cfop,
      });
      setZeroValueValidation(validation);

      // Get product fiscal data from form
      const productFiscal = {
        icmsOrigin: form.getFieldValue('icmsOrigin'),
        icmsCst: form.getFieldValue('icmsCst'),
        icmsCsosn: form.getFieldValue('icmsCsosn'),
        icmsRate: form.getFieldValue('icmsRate'),
        pisCst: form.getFieldValue('pisCst'),
        pisRate: form.getFieldValue('pisRate'),
        cofinsCst: form.getFieldValue('cofinsCst'),
        cofinsRate: form.getFieldValue('cofinsRate'),
        ipiCst: form.getFieldValue('ipiCst'),
        ipiRate: form.getFieldValue('ipiRate'),
      };

      // Calculate taxes - pass documentType to handle NFC-e specifics
      const taxes = fiscalService.calculateItemTaxes({
        totalValue,
        fiscalConfig,
        productFiscal,
        documentType: documentType || 'nfe',
      });

      setCalculatedTaxes(taxes);

      // Update form with calculated tax values
      // Set BOTH nested paths (used by handleFinish) and flat names (used by Step2 display fields)
      const taxFormValues = {
        'taxes.icms.value': taxes.icms?.value || 0,
        'taxes.icms.baseValue': taxes.icms?.baseValue || 0,
        'taxes.pis.value': taxes.pis?.value || 0,
        'taxes.cofins.value': taxes.cofins?.value || 0,
        'taxes.totalTaxes': taxes.totalTaxes || 0,
        // Flat names read by Step2 disabled display fields
        icmsValue: taxes.icms?.value || 0,
        pisValue: taxes.pis?.value || 0,
        cofinsValue: taxes.cofins?.value || 0,
        ipiValue: taxes.ipi?.value || 0,
      };

      // Only include IPI for NFe (not for NFC-e)
      if (!isNfce && taxes.ipi) {
        taxFormValues['taxes.ipi.value'] = taxes.ipi.value || 0;
      }

      form.setFieldsValue(taxFormValues);
    };

    return (
      <ProductStepContent
        selectedProduct={selectedProduct}
        onValueChange={recalculateTotalAndTaxes}
        zeroValueValidation={zeroValueValidation}
        documentType={documentType}
        subtotal={subtotal}
        form={form}
        onProductSelect={product => {
          setSelectedProduct(product);

          if (product && form) {
            // Determine tax regime for CST/CSOSN selection
            const taxRegime = fiscalConfig?.taxRegime || 'simples_nacional';
            const isSimples =
              typeof taxRegime === 'string'
                ? taxRegime.includes('simples')
                : [1, 2, 4].includes(taxRegime);

            // Extract fiscal data from product (can be at root or in fiscalData)
            const productFiscalData = product.fiscalData || {};

            // Get default CFOP (product > fiscalData > issuer > system default)
            const defaultCfop =
              product.cfop || productFiscalData.cfop || fiscalConfig?.defaultCfop || '5102';

            // Get NCM and CEST (product > fiscalData)
            const productNcm = product.ncm || productFiscalData.ncm || null;
            const productCest = product.cest || productFiscalData.cest || null;

            // Get CST/CSOSN defaults based on tax regime
            // For Simples Nacional: use CSOSN (102 = exempt, 500 = ST, etc.)
            // For Regime Normal: use CST (00, 10, 20, 40, 60, etc.)
            const icmsCst =
              product.icmsCst ??
              productFiscalData.icms?.cst ??
              fiscalConfig?.defaultIcmsCst ??
              '00';
            const icmsCsosn =
              product.icmsCsosn ??
              productFiscalData.icms?.csosn ??
              fiscalConfig?.defaultIcmsCsosn ??
              '102';
            const pisCst =
              product.pisCst ?? productFiscalData.pis?.cst ?? fiscalConfig?.defaultPisCst ?? '49';
            const cofinsCst =
              product.cofinsCst ??
              productFiscalData.cofins?.cst ??
              fiscalConfig?.defaultCofinsCst ??
              '49';

            // Get rates (product > fiscalData > issuer > defaults)
            const icmsRate =
              product.icmsRate ??
              productFiscalData.icms?.rate ??
              fiscalConfig?.defaultIcmsRate ??
              0;
            const pisRate =
              product.pisRate ?? productFiscalData.pis?.rate ?? fiscalConfig?.defaultPisRate ?? 0;
            const cofinsRate =
              product.cofinsRate ??
              productFiscalData.cofins?.rate ??
              fiscalConfig?.defaultCofinsRate ??
              0;
            const ipiRate =
              product.ipiRate ?? productFiscalData.ipi?.rate ?? fiscalConfig?.defaultIpiRate ?? 0;

            // Get ICMS origin (product > fiscalData > default)
            const icmsOrigin = product.icmsOrigin ?? productFiscalData.icms?.origin ?? 0;

            // Get IPI CST (product > fiscalData > default)
            const ipiCst = product.ipiCst ?? productFiscalData.ipi?.cst ?? '50';

            // Calculate initial total
            const quantity = form.getFieldValue('quantity') || 1;
            // Price can be in product.price, product.pricing.salePrice, or product.pricing.costPrice
            // Handle object format (Brazilian fiscal: {comercial, tributavel})
            const rawPrice =
              product.price || product.pricing?.salePrice || product.pricing?.costPrice || 0;
            const unitValue = typeof rawPrice === 'object' && rawPrice !== null
              ? (rawPrice.comercial ?? rawPrice.tributavel ?? 0)
              : (rawPrice ?? 0);

            // Handle unit object format:
            // - Hydrated from lookup: { uid, name, description }
            // - Legacy Brazilian fiscal: { comercial, tributavel }
            const rawUnit = product.unit || 'UN';
            const unitStr = typeof rawUnit === 'object' && rawUnit !== null
              ? (rawUnit.name ?? rawUnit.comercial ?? rawUnit.tributavel ?? 'UN')
              : (rawUnit ?? 'UN');

            // Handle description object format
            const rawDescription = product.description || '';
            const descriptionStr = typeof rawDescription === 'object' && rawDescription !== null
              ? (rawDescription.comercial ?? rawDescription.tributavel ?? '')
              : (rawDescription ?? '');

            const discount = form.getFieldValue('discount') || {
              init: 'percentage',
              percentage: 0,
              value: 0,
            };

            // Calculate subtotal and apply discount
            const itemSubtotal = financialService.roundCurrency(quantity * unitValue);
            const discountValue = discount.value || 0;
            const totalValue = financialService.roundCurrency(
              Math.max(0, itemSubtotal - discountValue)
            );

            // Pre-fill form with product data and fiscal defaults
            const formValuesToSet = {
              productId: product._id,
              productCode: product.sku || product.code,
              productName: product.name,
              description: descriptionStr,
              ncm: productNcm,
              cest: productCest,
              ean: product.ean,
              brand: product.brand,
              unitValue,
              unit: unitStr,
              totalValue,
              // Fiscal data with intelligent defaults
              cfop: defaultCfop,
              icmsOrigin,
              icmsCst,
              icmsCsosn,
              icmsRate,
              pisCst,
              pisRate,
              cofinsCst,
              cofinsRate,
              ipiCst,
              ipiRate,
            };

            form.setFieldsValue(formValuesToSet);

            // Build product fiscal data for tax calculation
            const productFiscal = {
              icmsOrigin,
              icmsCst,
              icmsCsosn,
              icmsRate,
              pisCst,
              pisRate,
              cofinsCst,
              cofinsRate,
              ipiCst,
              ipiRate,
            };

            // Calculate taxes - pass documentType to handle NFC-e specifics
            const taxes = fiscalService.calculateItemTaxes({
              totalValue,
              fiscalConfig,
              productFiscal,
              documentType: documentType || 'nfe',
            });

            // Store calculated taxes for Step2 and handleFinish
            setCalculatedTaxes(taxes);

            // Update form with calculated tax values
            // Set BOTH nested paths and flat names (Step2 display fields use flat names)
            const taxFormValues = {
              'taxes.icms.value': taxes.icms?.value || 0,
              'taxes.icms.baseValue': taxes.icms?.baseValue || 0,
              'taxes.pis.value': taxes.pis?.value || 0,
              'taxes.cofins.value': taxes.cofins?.value || 0,
              'taxes.totalTaxes': taxes.totalTaxes || 0,
              icmsValue: taxes.icms?.value || 0,
              pisValue: taxes.pis?.value || 0,
              cofinsValue: taxes.cofins?.value || 0,
              ipiValue: taxes.ipi?.value || 0,
            };

            // Only include IPI for NFe (not for NFC-e)
            if (!isNfce && taxes.ipi) {
              taxFormValues['taxes.ipi.value'] = taxes.ipi.value || 0;
            }

            form.setFieldsValue(taxFormValues);
          } else if (!product && form) {
            // Clear form when product is deselected
            form.setFieldsValue({
              productId: undefined,
              productCode: undefined,
              productName: undefined,
              description: undefined,
              ncm: undefined,
              cest: undefined,
              ean: undefined,
              brand: undefined,
              unitValue: 0,
              unit: 'UN',
              totalValue: 0,
              discount: { init: 'percentage', percentage: 0, value: 0 },
              cfop: undefined,
              icmsOrigin: 0,
              icmsCst: undefined,
              icmsCsosn: undefined,
              icmsRate: 0,
              pisCst: undefined,
              pisRate: 0,
              cofinsCst: undefined,
              cofinsRate: 0,
              ipiCst: undefined,
              ipiRate: 0,
              // Clear calculated taxes
              'taxes.icms.value': 0,
              'taxes.icms.baseValue': 0,
              'taxes.pis.value': 0,
              'taxes.cofins.value': 0,
              'taxes.ipi.value': 0,
              'taxes.totalTaxes': 0,
            });
            setCalculatedTaxes(null);
            setZeroValueValidation(null);
          }
        }}
      />
    );
  },
  onNext: ({ form }) => {
    // Recalculate taxes before advancing to Step2 (in case quantity/discount changed)
    if (!form) return true;

    const quantity = form.getFieldValue('quantity') || 1;
    const unitValue = form.getFieldValue('unitValue') || 0;
    const discount = form.getFieldValue('discount') || {
      init: 'percentage',
      percentage: 0,
      value: 0,
    };

    // Calculate subtotal and apply discount
    const itemSubtotal = financialService.roundCurrency(quantity * unitValue);
    const discountValue = discount.value || 0;
    const totalValue = financialService.roundCurrency(Math.max(0, itemSubtotal - discountValue));

    // Validate zero-value before allowing advance
    const cfop = form.getFieldValue('cfop');
    const validation = fiscalService.validateItemZeroValue({
      documentType: documentType || 'nfe',
      totalValue,
      cfop,
    });

    // Update validation state
    setZeroValueValidation(validation);

    // Block advance if validation fails (isValid = false)
    if (!validation.isValid) {
      return false;
    }

    // Get product-specific fiscal data from form (including CST/CSOSN)
    const productFiscal = {
      icmsOrigin: form.getFieldValue('icmsOrigin'),
      icmsCst: form.getFieldValue('icmsCst'),
      icmsCsosn: form.getFieldValue('icmsCsosn'),
      icmsRate: form.getFieldValue('icmsRate'),
      pisCst: form.getFieldValue('pisCst'),
      pisRate: form.getFieldValue('pisRate'),
      cofinsCst: form.getFieldValue('cofinsCst'),
      cofinsRate: form.getFieldValue('cofinsRate'),
      ipiCst: form.getFieldValue('ipiCst'),
      ipiRate: form.getFieldValue('ipiRate'),
    };

    // Calculate taxes - pass documentType to handle NFC-e specifics
    const taxes = fiscalService.calculateItemTaxes({
      totalValue,
      fiscalConfig,
      productFiscal,
      documentType: documentType || 'nfe',
    });

    setCalculatedTaxes(taxes);

    // Update form with calculated values
    // Set BOTH nested paths and flat names (Step2 display fields use flat names)
    const taxFormValues = {
      totalValue,
      'taxes.icms.value': taxes.icms?.value || 0,
      'taxes.icms.baseValue': taxes.icms?.baseValue || 0,
      'taxes.pis.value': taxes.pis?.value || 0,
      'taxes.cofins.value': taxes.cofins?.value || 0,
      'taxes.totalTaxes': taxes.totalTaxes || 0,
      icmsValue: taxes.icms?.value || 0,
      pisValue: taxes.pis?.value || 0,
      cofinsValue: taxes.cofins?.value || 0,
      ipiValue: taxes.ipi?.value || 0,
    };

    // Only include IPI for NFe (not for NFC-e)
    if (!isNfce && taxes.ipi) {
      taxFormValues['taxes.ipi.value'] = taxes.ipi.value || 0;
    }

    form.setFieldsValue(taxFormValues);

    return true; // Advance to next step
  },
  onEnter: ({ goFocus }) => {
    // Focus on product search field when step is entered
    setTimeout(() => goFocus?.('productId'), 100);
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default productStep;
